<template>
  <div class="certification col-xl-5 mt-4 mx-auto">
    <b-form-group>
      <b-form-row>
        <b-form-group label="Name" class="col-6" :state="IsFieldValid($v.submission.certificationName)" :invalid-feedback="RequiredAndMaxLengthMessage('Name', $v.submission.certificationName)">
          <b-form-input v-model="$v.submission.certificationName.$model" :state="IsFieldValid($v.submission.certificationName)" :disabled="isViewOnly" />
        </b-form-group>
        <b-form-group label="Title" class="col-6" :state="IsFieldValid($v.submission.certificationTitle)" :invalid-feedback="RequiredAndMaxLengthMessage('Title', $v.submission.certificationTitle)">
          <b-form-input v-model="$v.submission.certificationTitle.$model" :state="IsFieldValid($v.submission.certificationTitle)" :disabled="isViewOnly" />
        </b-form-group>
      </b-form-row>
    </b-form-group>

    <b-form-group>
      <b-form-row>
        <b-form-group label="Email" class="col-6" :state="IsFieldValid($v.submission.certificationEmail)" :invalid-feedback="emailMessage('Email', $v.submission.certificationEmail)">
          <b-form-input type="email" v-model="$v.submission.certificationEmail.$model" :state="IsFieldValid($v.submission.certificationEmail)" :disabled="isViewOnly" />
        </b-form-group>
        <b-form-group label="Confirm Email" class="col-6" :state="IsFieldValid($v.confirmEmail)" invalid-feedback="Emails must match.">
          <b-form-input type="email" v-model="$v.confirmEmail.$model" :disabled="isViewOnly" />
        </b-form-group>
      </b-form-row>
    </b-form-group>

    <b-form-group>
      <b-form-row>
        <b-form-group label="Entity Name" class="col-6" :state="IsFieldValid($v.submission.certificationCompany)" :invalid-feedback="RequiredAndMaxLengthMessage('Entity Name', $v.submission.certificationCompany)">
          <b-form-input v-model="$v.submission.certificationCompany.$model" :state="IsFieldValid($v.submission.certificationCompany)" :disabled="isViewOnly" />
        </b-form-group>
        <b-form-group label="Phone Number" class="col-6" :state="IsFieldValid($v.submission.certificationPhone)" :invalid-feedback="RequiredAndPhoneMessage($v.submission.certificationPhone)">
          <b-form-input v-model="$v.submission.certificationPhone.$model" :state="IsFieldValid($v.submission.certificationPhone)" :disabled="isViewOnly" />
        </b-form-group>
      </b-form-row>
    </b-form-group>

    <b-form-group label="Comments" :state="IsFieldValid($v.submission.certificationComment)" :invalid-feedback="MaxLengthMessage('Comments', $v.submission.certificationComment.$params.maxLength.max)">
      <b-form-textarea v-model="$v.submission.certificationComment.$model" :state="IsFieldValid($v.submission.certificationComment)" :disabled="isViewOnly"></b-form-textarea>
    </b-form-group>

    <b-form-checkbox
      id="legalOfficerOrRepresentative"
      v-model="isLegalOfficerOrRepresentative"
      class="text-left mb-3"
      :disabled="isViewOnly">
      I certify that I am a legal officer or authorized representative of one or more entities involved in this ownership
      change and will retain a copy of the signed ownership form in my possession for a period of not less than 5 years.
    </b-form-checkbox>

    <b-form-checkbox
      id="submissionCompleteAndCorrect"
      v-model="isSubmissionCompleteAndCorrect"
      class="text-left mb-3"
      :disabled="isViewOnly">I certify all information submitted in the ownership change(s) as complete and correct.</b-form-checkbox>

    <b-button @click="showSubmitModal" variant="success" :disabled="isSubmitDisabled" v-if="!isViewOnly">Submit</b-button>

    <b-modal ref="submit-modal" size="lg" title="Manage Ownership Certification and Agreement" header-text-variant="dark" body-text-variant="dark" centered>
      <template slot="default">
        This Certification and Agreement relates to the Compensation Rating and Inspection Bureau’s NJCRIB Manage Ownership application. 
        By clicking the “Accept” button below, or otherwise by using the NJCRIB Manage Ownership application, you hereby agree, 
        on your own behalf and on behalf of the organization you are employed by or otherwise represent, to the supplemental terms below:
        <ol>
          <li>
            You hereby certify that you are either:  A) the employer subject to this ownership form; or B) the current carrier of record 
            or producer of record for the employer organization (“Employer”). If you do not meet the requirements specified above, 
            you are required to select the “Decline” button below and not use this application.
          </li>
          <li>
            You agree to defend, indemnify, and hold NJCRIB and its directors, employees, and affiliates harmless from any and all claims, suits, 
            actions, losses, damages, costs, and expenses (including reasonable attorneys’ fees) arising from or relating to your breach of this 
            Certification and Agreement, including without limitation your failure to meet the certification requirements above.
          </li>
          <li>
            You acknowledge that NJCRIB may electronically capture and store your agreement to these terms along with your identifying information, 
            which will link your acceptance with your email address.
          </li>
        </ol>
      </template>
      <template slot="modal-footer" class="float-right">
        <b-alert show variant="danger" v-if="submittingError">There was an error while attempting to submit.</b-alert>
        <b-button variant="danger" @click="hideSubmitModal">Decline</b-button>
        <b-button variant="success" @click="confirmSubmit" class="ml-1" :disabled="isCurrentlySubmitting">
          <span v-if="!isCurrentlySubmitting">Accept</span>
          <span v-else><b-spinner small></b-spinner> Please Wait...</span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { required, maxLength, sameAs, email } from 'vuelidate/lib/validators'
import SubmissionService from '../services/SubmissionService.js'
import CustomValidations from '../mixins/customValidations.js'
const phoneValidation = CustomValidations.phoneValidation

export default {
  name: 'Certification',
  props: {
    submissionProp: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      submission: this.submissionProp,
      confirmEmail: '',
      isLegalOfficerOrRepresentative: false,
      isSubmissionCompleteAndCorrect: false,
      isCurrentlySubmitting: false,
      submittingError: false
    }
  },
  validations: {
    submission: {
      certificationName: {
        required,
        maxLength: maxLength(50)
      },
      certificationTitle: {
        required,
        maxLength: maxLength(50)
      },
      certificationEmail: {
        required,
        email,
        maxLength: maxLength(50)
      },
      certificationCompany: {
        required,
        maxLength: maxLength(100)
      },
      certificationPhone: {
        required,
        phoneValidation
      },
      certificationComment: {
        maxLength: maxLength(2000)
      }
    },    
    confirmEmail: {
      sameAsEmail: sameAs(function() {
        return this.submission.certificationEmail
      })
    },
  },
  computed: {
    isViewOnly() {
      return this.$store.getters['application/isSubmissionViewOnly']
    },
    isSubmitDisabled() {
      return !this.isLegalOfficerOrRepresentative || !this.isSubmissionCompleteAndCorrect
    }
  },
  methods: {
    emailMessage(name) {
      const field = this.$v.submission.certificationEmail
      return !field.required ? this.RequiredMessage(name) : !field.email ? `${name} must be a valid email.` : this.MaxLengthMessage(name, field.$params.maxLength.max)
    },
    showSubmitModal() {
      this.submittingError = false      
      this.$v.$touch()
      if (this.$v.$invalid) return

      this.$refs['submit-modal'].show()
    },
    hideSubmitModal() {
      this.$refs['submit-modal'].hide()
    },
    confirmSubmit() {
      this.isCurrentlySubmitting = true
      SubmissionService.submit(this.submission).then(res => {
        this.isCurrentlySubmitting = false
        this.$store.dispatch('submission/update', res.data).then(() => {
          this.$router.push({
            name: 'submission/list'
          })
        })
      }).catch(() => {
        this.isCurrentlySubmitting = false
        this.submittingError = true
      })
    },
    backClicked() {
      this.$router.push({
        name: 'review',
        params: {
          submissionId: this.submission.submissionID
        }
      })
    }
  },
  created() {
    this.$EventBus.$on('back-clicked', this.backClicked)
  },
  beforeDestroy() {
    this.$EventBus.$off('back-clicked', this.backClicked)
  }
}
</script>